import type {
  CallMeta,
  CauseField,
  HttpErrorLogFields,
  HttpFailureKind,
  RequestOptions,
  SanitizeErrorFields,
} from './types';

/*
 * `@ns/http-request` — the error hierarchy, the runtime half.
 *
 * `types/` carries the shape and nothing else. It used to carry these classes
 * too, as `export declare`, which is a type-level promise only: measured,
 * `Object.keys(require('./types'))` is `[]`, so nothing it declared existed at
 * runtime and a caller reaching for one got `is not a function`. Everything in
 * this file is real: it is what the pipeline actually throws and what a catch
 * site actually holds.
 *
 * TWO LAYERS and six typeguards, all built. `HttpError` carries what every
 * failure has; `HttpRequestError` adds what only exists when an answer came
 * back, and is concrete because a status with no name of its own is thrown as
 * it. Two failures sit outside that hierarchy altogether -- the breaker's and
 * the bulkhead's refusals, which this file only re-exports -- and
 * `errors.test.ts` reads that list off the entry point rather than repeating it
 * here, so a third one shows up on its own.
 */

/*
 * WHY BOTH A CLASS HIERARCHY AND A `failureKind`.
 *
 * These two mechanisms overlap on purpose: a class per failure next to a
 * coarser `failureKind`. That overlap is expensive to undo, which is why it is
 * spelled out here: adding a class is additive, but removing or renaming one
 * breaks every `instanceof` written against it.
 *
 * Measured for comparison: axios ships ONE error class plus a `code` field
 * (`ERR_BAD_REQUEST`, `ERR_NETWORK`, `ERR_FR_TOO_MANY_REDIRECTS`, ...), so the
 * pruned shape is a real alternative rather than a hypothetical one.
 *
 * The classes stay, and the reason is dispatch. A class lets a caller branch on
 * the type and then reach for whatever that specific failure carries — its own
 * fields, its own message — without first proving which shape it is holding:
 *
 * ```ts
 * catch (e) {
 *   if (e instanceof NotFoundError) return null;
 *   if (e instanceof CircuitOpenError) return cached(e.openedAt);
 *   logger.error(msg, { kind: e.failureKind });
 * }
 * ```
 *
 * So the two mechanisms answer different questions. `instanceof` is for a catch
 * chain that HANDLES a case; `failureKind` is a closed, stable string for
 * logging, metrics and grouping, where a class name would be a moving target.
 *
 * Twelve of the classes carry no fields of their own, seven of those being plain
 * HTTP statuses that `err.status` also expresses. They are kept anyway, because
 * pruning them later is breaking while keeping them costs a name.
 */

/*
 * Four fields on the base class are NON-ENUMERABLE at runtime: `headers`,
 * `body`, `options` and `cause`. They are the ones that carry secrets, and
 * `hide()` below installs them. `cause` gets it for free: `Error`
 * installs it non-enumerable itself, from the `options` argument of its
 * constructor, which is why `hide()` copies that descriptor rather than
 * inventing one.
 *
 * The reason is one line people write without thinking:
 *
 *   logger.error('upstream failed', { ...error });
 *
 * That spread reads like "give me the details" and leaks the lot: bearer
 * tokens and cookies from `options.headers`, the complete response body,
 * `set-cookie` from `headers`, and whatever the raw `cause` drags along.
 *
 * What a response failure carrying a `set-cookie` header, a body and a reason
 * phrase gives back:
 *   e.headers        -> {"set-cookie":"..."}             catch site keeps it
 *   { ...e }         -> ["method","url","failureKind","status","statusText"]
 *   Object.keys(e)   -> the same five
 *   e.toJSON()       -> ["errorClass","errorMessage","failureKind","method",
 *                        "url","status","statusText"]
 *   getOwnPropertyNames(e)
 *                    -> everything, including the four. See the vangnet note.
 *
 * So the four stay out of the spread AND out of `toJSON()`: the serialized form
 * is `logFields()`, which reads what a log wants rather than what the object
 * happens to hold. A deliberate reader gets them off the error itself.
 *
 * `statusText` is enumerable on purpose and is not one of the four: a reason
 * phrase carries no secret, and `HttpRequestError`'s constructor installs it
 * with `enumerable: true` rather than through `hide()`. It is there only when
 * the upstream sent a phrase. A failure on the bare branch has neither it nor
 * `status`, because both are declared on `HttpRequestError` and nowhere above
 * it.
 *
 * So nothing is taken away from a deliberate reader, and the accidental line
 * goes quiet. `url`, `status`, `method` and `failureKind` stay enumerable,
 * because those are exactly what you want in a log.
 *
 * This is a vangnet, not a boundary: `Object.getOwnPropertyNames`,
 * `util.inspect` and a debugger still show everything. Anyone writing those
 * knows what they are doing.
 *
 * NOT expressible in the type system — enumerability is a runtime property, so
 * the compiler sees no difference between a spread with and without it. Only a
 * test can hold this, so `error-spread.test.ts` builds every exported error
 * class and asserts that `{ ...error }` carries no key `logFields()` would not
 * report, rather than trusting the `hide()` calls to stay put.
 */

/** The third argument of every failure in this hierarchy. All of it optional. */
export interface HttpErrorOptions {
  /**
   * Which call this failure belongs to, and how long that caller waited.
   *
   * @remarks
   * Absent on a failure raised inside a SHARED flight before it is handed to a
   * caller, for the same reason `options` is absent there: at that moment it
   * belongs to no one in particular. Each participant is given a rebuilt
   * failure carrying its own.
   *
   * Both fields land on the error NON-ENUMERABLE, but not for the reason the
   * other four do: they carry no secret. The four enumerable fields are the
   * failure's identity, `{ ...error }` has a documented shape, and
   * `logFields()` is the route to everything else.
   */
  call?: CallMeta;
  /**
   * The ORIGINAL error, untouched. The portal logger walks this chain itself.
   * NON-ENUMERABLE: a raw cause drags credentials and network detail along.
   */
  cause?: unknown;
  /**
   * The options the failed call was made with, credentials included.
   * NON-ENUMERABLE: `options.headers` holds them.
   *
   * @remarks
   * Reading a variant-specific field needs a narrow first: `'raw' in options`
   * separates the two axes, and `options.raw === true` is the discriminant.
   * Reading `url`, `method` or `headers` — which is what a catch site almost
   * always wants — needs no narrowing, because those live in the shared base.
   *
   * @privateRemarks
   * This stays the four-member `RequestOptions` union. At a catch site you do
   * not know which variant the failed call was, and pretending otherwise would
   * be a lie the compiler cannot check.
   */
  options?: RequestOptions;
}

/**
 * The third argument of the RESPONSE branch, where `status` is not optional.
 *
 * @privateRemarks
 * That one required field is the whole point of this type. On the base it was
 * `status?: number` and both nonsense shapes compiled: a timeout carrying a
 * status, a 404 lacking one. Here it is unspeakable rather than discouraged.
 */
export interface HttpRequestErrorOptions extends HttpErrorOptions {
  /**
   * The response body, COMPLETE rather than truncated: a body cut off at a
   * fixed length loses exactly the field you needed. NON-ENUMERABLE, because it
   * may hold personal data.
   */
  body?: unknown;
  /** The response headers. NON-ENUMERABLE: carries `set-cookie`. */
  headers?: Headers;
  /** The response status. */
  status: number;
  /** The reason phrase beside the status, `''` when the upstream sent none. */
  statusText?: string;
}

/**
 * Installs a property that a spread will not pick up.
 *
 * Used for two things, and the second is the one nobody expects:
 *
 * 1. The four fields that carry secrets — `body`, `headers`, `options` and
 *    `cause`.
 * 2. `name`. The obvious line, `this.name = 'NotFoundError'`, installs an own
 *    ENUMERABLE property, where `Error.prototype.name` is neither own nor
 *    enumerable — so it puts a fifth key in `{ ...error }`. `name` carries no
 *    secret, but it is not one of the fields the spread is documented to carry
 *    either.
 *
 * The descriptor is the one the platform gives `Error.prototype.name`, and the
 * one `Error` itself gives `cause`: writable and configurable. So the four
 * fields behave identically whether we install them or the platform does, and a
 * subclass declaring a field of the same name does not hit `Cannot redefine
 * property`. Only `enumerable` is load-bearing.
 *
 * A class name is passed in as a literal rather than read from
 * `new.target.name`, because a minifier renames the class while the duck-typed
 * guards at the bottom of this file compare against exactly that string.
 */
function hide(target: object, key: string, value: unknown): void {
  Object.defineProperty(target, key, {
    configurable: true,
    enumerable: false,
    value,
    writable: true,
  });
}

/**
 * One read of `key`, so the value that gets checked is the value that is used.
 *
 * `Reflect.get` and not an index with a cast: it fires the same `get` trap and
 * needs no type assertion to reach a key the type does not declare. And no
 * `key in link` in front of it: `Reflect.get` answers `undefined` for an absent
 * key by itself, so the test would add a SECOND run of foreign code (the `has`
 * trap) without changing any answer -- measured as an equivalent mutant against
 * the whole suite. The one input where it did change the answer was a `has`
 * trap returning `false` over a readable value, and there it threw the value
 * away.
 */
function read(link: object, key: string): unknown {
  return Reflect.get(link, key);
}

/**
 * One link of the `cause` chain, flattened to what a log line can hold. The
 * platform extras are read defensively: `code`, `errno` and `syscall` are what
 * Node hangs on a socket error, and none of them is guaranteed.
 *
 * "Defensively" is about the TYPE AND about the read. All five properties
 * belong to a value that came from outside, so any of them may be a getter and
 * the value itself may be a `Proxy` whose `has` trap fires before any read.
 * `logFields()` and `JSON.stringify(error)` promise not to throw OVER THE CAUSE
 * CHAIN -- a `sanitize` you pass in is called unguarded and is a separate
 * matter, see its own `@throws`. An
 * absolute is worth only what enforces it, so a link that resists being read is
 * treated as a link that carries nothing (`ER55`). Same form and same reason as
 * `trace.ts` at `TR16`.
 *
 * The whole body is guarded and not each read on its own: a value that throws
 * on one property is a value whose other answers are not worth trusting either,
 * and an EMPTY link is what the caller can act on. A half-read link would say
 * more than it knows.
 */
function flattenLink(link: object): CauseField {
  try {
    // ONE read per property, into a local, and the check is on the local. The
    // shorter `'x' in link && typeof link.x === 'string' ? link.x : ''` reads
    // twice, and a `Proxy` may answer differently each time: measured with a
    // `get` trap returning `'ok'` and then `10n`, the `string`-typed field held
    // a `bigint`. The value that was checked has to BE the value that is used.
    const message = read(link, 'message');
    const name = read(link, 'name');
    const code = read(link, 'code');
    const errno = read(link, 'errno');
    const syscall = read(link, 'syscall');

    const causeField: CauseField = {
      message: typeof message === 'string' ? message : '',
      name: typeof name === 'string' ? name : '',
    };
    if (typeof code === 'string') causeField.code = code;
    if (typeof errno === 'number') causeField.errno = errno;
    if (typeof syscall === 'string') causeField.syscall = syscall;
    return causeField;
  } catch {
    return { message: '', name: '' };
  }
}

/**
 * Whether a `cause` is a thing with properties to read. A function counts: it
 * carries `name`, it can carry `cause`, and `String()` on one would dump its
 * source into a log line.
 *
 * @remarks
 * Exported for `retry.ts`, which walks the same chain and must not disagree
 * with this file about what a link IS. NOT re-exported from `index.ts`: two
 * modules sharing one definition is the point, a public guard is not.
 */
export function isCauseObject(link: unknown): link is object {
  return (
    link !== null && (typeof link === 'object' || typeof link === 'function')
  );
}

/**
 * The next link, or `undefined` when there is none. The membership test runs
 * the same foreign code as the reads in {@link flattenLink}, so it is guarded
 * for the same reason: a chain that resists being walked ends here.
 *
 * @remarks
 * Exported for `retry.ts` for the same reason as {@link isCauseObject}: that
 * file walks the same chain, and an unguarded read there would let a hostile
 * `cause` out through a path this one closes.
 */
export function nextCause(link: object): unknown {
  try {
    return 'cause' in link ? link.cause : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Installs the predicate's own error at the END of the chain the failure already
 * carries.
 *
 * At the end, and not on the failure itself. Writing to `failure.cause` REPLACED
 * whatever hung there, and on a refused connection that is the entire diagnosis:
 * `fetch failed` and the link underneath it carrying `ECONNREFUSED` and its
 * `syscall` both disappeared (ER54, ER56) -- at the one moment something has
 * already gone wrong. The tail is the cheapest slot, not a free one, and the
 * difference is new: this walk stops one link EARLIER than `flattenCause` does,
 * at the first value that is not an object, and `flattenCause` gives that value
 * a link of its own when it is a PRIMITIVE (ER55). For `null` and `undefined` it
 * gives none, which is why those are not a reason to refuse. So writing here would take away something a
 * reader can see, and when the value that stopped the walk is a PRIMITIVE the
 * library refuses the write and drops the predicate's error instead (ER58).
 *
 * The earlier version of this paragraph claimed parity with `flattenCause` and
 * that such a primitive `was never in causeFields to begin with`. Both were
 * true and stopped being true, which is why the claim is written down here as
 * retracted rather than quietly deleted.
 *
 * The `Set` bounds the walk for the reason ER53 gives: `err.cause = err` is
 * legal, and a walk without one never returns.
 *
 * The write is GUARDED, and walking is what made that necessary. The old target
 * was the top failure, which for a transport error is this library's own
 * `NetworkError`; the tail is whatever the platform or the caller threw, and a
 * frozen or sealed link cannot take a new property. MEASURED with a frozen link:
 * unguarded, the caller got `TypeError: Cannot define property cause, object is
 * not extensible` where the guarded version still gives them `NetworkError:
 * network request failed`. Dropping the predicate's error costs one link of
 * diagnosis, while letting the `TypeError` out costs the whole failure and
 * replaces it with a puzzle. The same unequal cost `worthRepeating` decides on.
 *
 * Non-enumerable, which is the descriptor `Error` itself gives `cause` and the
 * one DECISION 9.9 requires: a raw cause drags credentials and network detail
 * along, and a spread of the failure must not pick them up.
 *
 * One owner, two callers: `retry.ts` uses it the same way for a throwing
 * `shouldRetry`, and the delivery layer in `request.ts` uses it for a throwing
 * `throwOnError` predicate, on the same reasoning.
 */
export function attachCause(failure: unknown, cause: unknown): void {
  if (!isCauseObject(failure)) return;

  const seen = new Set<object>();
  let tail: object = failure;
  let link: unknown = failure;
  while (isCauseObject(link) && !seen.has(link)) {
    seen.add(link);
    tail = link;
    link = nextCause(link);
  }

  // THREE things stop that walk and only one of them means something would be
  // lost. The chain simply ENDED (`link` is `undefined` or `null`): the ordinary
  // case, nothing occupies the slot, write. An OBJECT stopped it: that is the
  // cycle guard of `ER53`, and the value is already IN the chain, so a
  // self-reference is not a link of its own and nothing is lost, write. A
  // PRIMITIVE stopped it: `ER55` gives THAT value a link in `causeFields`, so
  // writing over the slot takes it away, and `ER58` decides that the platform's
  // diagnosis outranks the predicate's error. Refuse.
  if (link !== undefined && link !== null && !isCauseObject(link)) return;

  try {
    Object.defineProperty(tail, 'cause', {
      configurable: true,
      enumerable: false,
      value: cause,
      writable: true,
    });
  } catch {
    // Dropped on purpose: see above. The caller keeps the failure they came for.
  }
}

/**
 * The whole `cause` chain, outermost first. No stack: that belongs in the error
 * itself, not in a meta object that ends up in every log line.
 *
 * The `Set` bounds the walk. `err.cause = err` is legal, and a loop over the
 * chain without one never returns.
 *
 * A PRIMITIVE cause gets a link of its own and ends the chain there. Ending is
 * a choice and not a fact about primitives: `String.prototype.cause` is legal,
 * so a string CAN answer that read. We stop anyway, because a chain reached
 * through a prototype belongs to whoever patched the prototype and not to the
 * failure being logged. A `fetch` that rejects with a bare
 * `'ECONNRESET'` is legal JavaScript, and that string is the whole reason: it
 * belongs in the log line and not only on `error.cause` (`ER55`).
 *
 * `null` and `undefined` get NO link. That is the ABSENCE of a cause, not a
 * cause without a name, and a link saying `'null'` would add a field to every
 * error that has none.
 */
function flattenCause(start: unknown): CauseField[] {
  const chain: CauseField[] = [];
  const seen = new Set<object>();

  let link: unknown = start;
  for (;;) {
    // One case per `typeof` result, so each branch narrows `link` and a
    // primitive reaches `String()` as a primitive, never as an object.
    switch (typeof link) {
      case 'undefined':
        return chain;
      case 'object':
      case 'function':
        // The cycle guard, unchanged: `err.cause = err` is legal, and a walk
        // without one never returns.
        if (link === null || seen.has(link)) return chain;
        seen.add(link);
        chain.push(flattenLink(link));
        link = nextCause(link);
        break;
      case 'string':
      case 'number':
      case 'boolean':
      case 'bigint':
      case 'symbol': {
        const primitive: string | number | boolean | bigint | symbol = link;
        chain.push({ message: String(primitive), name: '' });
        return chain;
      }
    }
  }
}

/**
 * The base of the error hierarchy: what EVERY failure of this library has.
 *
 * @remarks
 * Nothing about a response lives here. A refused connection, an elapsed
 * deadline and a body that would not decode all reach a catch site with nothing
 * but the call they came from. At most points in the pipeline there is no
 * response at all, which is what keeps the response half off the base and onto
 * `HttpRequestError`.
 */
export abstract class HttpError extends Error {
  /**
   * Which coarse family this failure belongs to: a closed, stable string for
   * logging, metrics and grouping, where a class name would be a moving target
   * under a minifier. Branch on the class when you want to HANDLE a case, read
   * this when you want to COUNT one.
   *
   * @remarks
   * Every class below fixes it to one literal, and several share one, so
   * narrowing on it groups siblings rather than picking one out -- `'http'`
   * covers the whole response branch. The table in `errors.test.ts` is where
   * the class-to-kind mapping is written down and checked. Enumerable: you want
   * this in your log.
   */
  abstract readonly failureKind: HttpFailureKind;

  /**
   * The method the failed call went out with, `'GET'` when the caller named
   * none. Read AFTER the `before` handlers, so it is the method that actually
   * went on the wire rather than the one the call site typed. Enumerable: you
   * want this in your log.
   */
  readonly method: string;

  /** The full URL. Enumerable: you want this in your log. */
  readonly url: string;

  /*
   * `options` below, and `body`, `headers` and `statusText` on the response
   * branch, are `declare` rather than plain fields, so that the `hide` calls in
   * the constructors are the only thing that installs them.
   *
   * MEASURED, because the tempting claim here is that `declare` is what keeps
   * them out of a spread, and it is not. Under `useDefineForClassFields` a
   * plain `readonly body?: unknown` emits `body;`, an own ENUMERABLE property
   * set to `undefined`; `declare` emits nothing at all. The spread is four keys
   * either way, because a class field is installed before the constructor body
   * runs and `hide` then redefines it.
   *
   * What `declare` buys is the failure mode. Drop a `hide` call and a declared
   * field leaves nothing behind, so a reader gets `undefined` and a test says
   * so. Drop it under a plain field and the property is still there, still
   * enumerable, still empty — the shape of a hidden field with none of its
   * effect.
   */

  /**
   * The options the failed call was made with, credentials included, or
   * `undefined` when the throw site had none to hand. This is the whole request
   * at a catch site: `url`, `method` and `headers` read straight off it, a
   * variant-specific field needs `'raw' in options` to narrow first.
   *
   * @remarks
   * NON-ENUMERABLE, so `{ ...error }` leaves it behind and `logFields()`
   * does not pass it on -- `options.headers` is where the bearer token lives.
   * @see HttpErrorOptions.options
   */
  declare readonly options?: RequestOptions;

  /**
   * Which call raised this, and how long that caller waited.
   *
   * @remarks
   * NON-ENUMERABLE, in the same shape as the four secret-carrying fields but
   * for a different reason: these carry no secret. They stay out of `{ ...error }`
   * because the enumerable set is the failure's identity and that spread has a
   * documented shape; `logFields()` reports them.
   *
   * Absent on a failure raised inside a shared flight.
   */
  declare readonly callId?: string;
  /**
   * How many attempts this call had STARTED when it failed.
   *
   * Reads the same way as `requests` below, and the two part company as soon as
   * an attempt strands before the transport. See `callId` for why it is hidden.
   */
  declare readonly attempts?: number;
  /**
   * How many requests had reached the LINE when this call failed.
   *
   * `0` says plainly that nothing was sent, which is why `requestId` is absent
   * alongside it rather than empty. See `callId` for why it is hidden.
   */
  declare readonly requests?: number;
  /**
   * The span this call put on the wire, when it got that far.
   *
   * Read back off the `traceparent` that went out, so it names a span a server
   * can be searched for. See `callId` for why it is hidden.
   */
  declare readonly spanId?: string;
  /**
   * The trace this call was part of, when it got that far.
   *
   * From the same header, which on a continued trace is NOT the one minted
   * locally. See `callId` for why it is hidden.
   */
  declare readonly traceId?: string;
  /** How long this caller waited, in milliseconds. See `callId`. */
  declare readonly durationMs?: number;
  /**
   * The request this call was part of, when one went out.
   *
   * @remarks
   * Coarser than `callId`: everyone who joined a flight behind this call shares
   * it, and so does every call inside one incoming request. Equal to `callId`
   * exactly when this library started the request itself.
   */
  declare readonly requestId?: string;

  constructor(
    message: string,
    method: string,
    url: string,
    options?: HttpErrorOptions,
  ) {
    super(message, options);
    this.method = method;
    this.url = url;
    hide(this, 'options', options?.options);
    hide(this, 'attempts', options?.call?.attempts);
    hide(this, 'callId', options?.call?.callId);
    hide(this, 'durationMs', options?.call?.durationMs);
    hide(this, 'requestId', options?.call?.requestId);
    hide(this, 'requests', options?.call?.requests);
    hide(this, 'spanId', options?.call?.spanId);
    hide(this, 'traceId', options?.call?.traceId);
  }

  /**
   * The log fields: flat, serializable, ready to spread into a meta object.
   *
   * @remarks
   * It reads the hidden fields without trouble, because it is our own function
   * rather than a spread, which is the whole point of hiding them. Of them it
   * passes on only the flattened `cause`; the headers, the body and the request
   * options stay behind, since those are the ones that carry the secret.
   *
   * Without an argument nothing is filtered and the URL goes out whole. Pass a
   * sanitizer to decide what survives.
   *
   * The sanitizer lives HERE and not on `toJSON`, and it has to stay that way:
   * `toJSON` is a name the language owns, and `JSON.stringify` calls it WITH
   * the property key the value hangs under. A parameter in that slot therefore
   * receives the string `"error"` from `JSON.stringify({ error })` and gets
   * called as a function.
   * @throws Whatever `sanitize` throws. It is your function and it is called
   *   unguarded, so a sanitizer that fails takes down the catch block that was
   *   trying to log the failure. `toJSON()` cannot hit that one: it takes no
   *   sanitizer and passes none on.
   * @remarks
   * Reading the `cause` chain does NOT throw, and that is enforced rather than
   * hoped for. Flattening it reads `message`, `name`, `code`, `errno`,
   * `syscall` and `cause` off values that came from outside this library, so a
   * throwing getter or a `Proxy` trap among them is expected. A link that
   * RESISTS being read yields an empty link, and a link that LIES between two
   * reads cannot: every property is read once and the check is on what came
   * back. `toJSON()` and
   * `JSON.stringify(error)` go through here and inherit that.
   * @privateRemarks
   * `ER55`.
   */
  logFields(sanitize?: SanitizeErrorFields): HttpErrorLogFields {
    const fields: HttpErrorLogFields = {
      errorClass: this.name,
      errorMessage: this.message,
      failureKind: this.failureKind,
      method: this.method,
      url: this.url,
    };

    const causeFields = flattenCause(this.cause);
    if (causeFields.length > 0) fields.causeFields = causeFields;

    // Set only when present, so a failure from inside a shared flight reports
    // absence rather than a `callId: undefined` a reader has to interpret.
    if (this.attempts !== undefined) fields.attempts = this.attempts;
    if (this.callId !== undefined) fields.callId = this.callId;
    if (this.durationMs !== undefined) fields.durationMs = this.durationMs;
    if (this.requestId !== undefined) fields.requestId = this.requestId;
    if (this.requests !== undefined) fields.requests = this.requests;
    if (this.spanId !== undefined) fields.spanId = this.spanId;
    if (this.traceId !== undefined) fields.traceId = this.traceId;

    return sanitize ? sanitize(fields) : fields;
  }

  /**
   * What `JSON.stringify` serializes a failure to: the unsanitised log fields.
   *
   * @remarks
   * NO parameters, deliberately — see `logFields` for what arrives in that slot
   * if you give it one. `logFields` is virtual, so a subclass that adds a field
   * gets it here too without overriding this method.
   */
  toJSON(): HttpErrorLogFields {
    return this.logFields();
  }
}

/**
 * The upstream ANSWERED, and the answer is the failure. Carries the response.
 *
 * @remarks
 * Whether a failure here is worth repeating is a property of the STATUS and not
 * of the class. The default list is `[408, 429, 502, 503, 504]`, the answers
 * that say "not now" rather than "not this request". Every other status is left
 * alone because the same request gets the same answer, which is why the named
 * 4xx children all read "not retryable".
 *
 * CONCRETE, and that is load-bearing twice over. It is what a status with no
 * name of its own is thrown as: a 409, a 451, the case a status switch would
 * hand to its `default:`. And it supplies `failureKind` to its eight named
 * children, so none of them repeats what the branch already is — everything
 * reachable from here came back with a status, and a status failure is
 * `'http'`.
 */
export class HttpRequestError extends HttpError {
  /**
   * Always `'http'`: everything reachable from this branch came back with a
   * status, and a status failure is one kind.
   *
   * @remarks
   * The eight named children inherit it rather than declare it again, so
   * this one literal covers nine classes and cannot tell them apart. Read
   * `status` for the number, or use `instanceof` when you mean one of them.
   *
   */
  readonly failureKind = 'http';

  /**
   * The response status. Enumerable: a log wants it.
   *
   * @privateRemarks
   * Not optional, unlike the `status?: number` this replaced on the base. The
   * specification asked for `-1` when nothing came back; the branch answers
   * that question differently and better — a failure with nothing to report is
   * not on this branch at all, so there is no absence left to encode.
   */
  readonly status: number;

  /**
   * The body that came with the failing response, decoded the same way a
   * successful one would have been. COMPLETE rather than truncated: a body cut
   * off at a fixed length loses exactly the field you needed.
   *
   * @remarks
   * `unknown`, and that is honest: `validate.response` describes what a
   * SUCCESSFUL answer looks like, so it is deliberately NOT run on an error
   * body. Narrow it yourself. `undefined` when nothing was decoded -- a `HEAD`,
   * or a `raw` call, where the unread stream IS the answer and what happens to
   * it is the caller's decision.
   *
   * NON-ENUMERABLE, because an error body may hold personal data: a
   * spread does not pick it up and `logFields()` does not pass it on. Reach for
   * it deliberately, and decide yourself what of it may be logged.
   * @see HttpRequestErrorOptions.body
   */
  declare readonly body?: unknown;

  /**
   * The response headers, as the upstream sent them. This is where a `location`
   * on a redirect you did not follow, or a vendor-specific error code, is still
   * readable after the fact. `HttpThrottleError` has already read `retry-after`
   * off it for you.
   *
   * @remarks
   * NON-ENUMERABLE: it carries `set-cookie`.
   * @see HttpRequestErrorOptions.headers
   */
  declare readonly headers?: Headers;

  /**
   * The reason phrase beside the status, `''` when the upstream sent none.
   * Never branch on it -- it is free text the upstream chose, where `status` is
   * the number the standard fixed.
   *
   * @remarks
   * ENUMERABLE, unlike the four fields `hide()` installs, and a log field in its
   * own right. A reason phrase carries no secret, so there is no reason for
   * `{ ...error }` or `JSON.stringify(error)` to drop it. The constructor
   * installs the key only when the upstream sent a phrase, so an error that has
   * none carries no empty slot rather than a `statusText: undefined` a reader
   * has to interpret.
   *
   * @see HttpRequestErrorOptions.statusText
   */
  declare readonly statusText?: string;

  constructor(
    message: string,
    method: string,
    url: string,
    options: HttpRequestErrorOptions,
  ) {
    super(message, method, url, options);
    this.status = options.status;
    hide(this, 'body', options.body);
    hide(this, 'headers', options.headers);
    // Enumerable, unlike the two above: a reason phrase carries no secret, so
    // there is no reason for `JSON.stringify(error)` to drop it. Installed only
    // when there is one, so an upstream that sent none leaves no empty key.
    if (options.statusText !== undefined) {
      Object.defineProperty(this, 'statusText', {
        configurable: true,
        enumerable: true,
        value: options.statusText,
        writable: true,
      });
    }
    hide(this, 'name', 'HttpRequestError');
  }

  /**
   * The base fields plus the status.
   *
   * @remarks
   * `super.logFields()` is called WITHOUT the sanitizer on purpose, so that the
   * sanitizer is handed the complete set once instead of a version that still
   * grows a field afterwards.
   *
   * There is no `toJSON` override alongside this one: the base calls
   * `this.logFields()`, so the status arrives in a stringified failure through
   * this method.
   * @throws Whatever `sanitize` throws. It is your function and it is called
   *   unguarded, so a sanitizer that fails takes down the catch block that was
   *   trying to log the failure. `toJSON()` cannot hit that one: it takes no
   *   sanitizer and passes none on.
   * @remarks
   * Reading the `cause` chain does NOT throw, and that is enforced rather than
   * hoped for. Flattening it reads `message`, `name`, `code`, `errno`,
   * `syscall` and `cause` off values that came from outside this library, so a
   * throwing getter or a `Proxy` trap among them is expected. A link that
   * RESISTS being read yields an empty link, and a link that LIES between two
   * reads cannot: every property is read once and the check is on what came
   * back. `toJSON()` and
   * `JSON.stringify(error)` go through here and inherit that.
   * @privateRemarks
   * `ER55`.
   */
  override logFields(sanitize?: SanitizeErrorFields): HttpErrorLogFields {
    const fields: HttpErrorLogFields = {
      ...super.logFields(),
      status: this.status,
    };
    if (this.statusText !== undefined) fields.statusText = this.statusText;
    return sanitize ? sanitize(fields) : fields;
  }
}

/* ---------------------------------------------------------------------------
 * Caught before anything was sent. `unsupported`, not `http`: there was never a
 * request, let alone a status.
 * ------------------------------------------------------------------------- */

/**
 * The third argument of `UnsupportedRequestError`, where the reason fits when
 * no exception is carrying it.
 *
 * @privateRemarks
 * Its own interface rather than a field on the base, for the reason
 * `HttpRequestErrorOptions` exists: a base that carried it would let every
 * failure in this file claim a reason it never has.
 */
export interface UnsupportedRequestErrorOptions extends HttpErrorOptions {
  /**
   * Why the call cannot be made, appended to the message so `String(failure)`
   * says it out loud.
   *
   * @remarks
   * A string the throw site WROTE, never one read off the failed call, and that
   * is the safety half of the decision. Deriving the reason from `cause` instead
   * would name it for free at every throw site here, but those causes come from
   * `new URL()` and `Headers.set()`, which quote the value they choked on — so a
   * bearer token or a password in userinfo would land in the one field that goes
   * into every log line, and that is exactly the residual risk we accept for a
   * url the caller wrote themselves.
   *
   * It is spent on the message and kept nowhere else, so it adds no field to the
   * instance and none to `logFields()`, so there is nothing here to hide.
   *
   */
  detail?: string;
}

/**
 * A handler cannot serve this call, or the call itself does not hold up. Caught
 * before anything was sent. Not retryable.
 *
 * @remarks
 * Usually wraps the platform `TypeError` that spotted it — `new URL()` for the
 * address, `Headers.set()` for a header — so the reason survives in `cause`
 * while the caller sees one recognizable failure instead of a bare `TypeError`
 * that reads like a network failure. Where the library itself decides, there is
 * no such `TypeError` to wrap, and `detail` is where that reason goes.
 */
export class UnsupportedRequestError extends HttpError {
  /**
   * Always `'unsupported'`: nothing was sent, so there is no status to group on
   * and no point retrying. Shared with `UnsupportedBodyError`, which is the
   * other way a call fails to hold up before it leaves.
   */
  readonly failureKind = 'unsupported';

  constructor(
    method: string,
    url: string,
    options?: UnsupportedRequestErrorOptions,
  ) {
    // The reason is APPENDED, so the message every existing throw site produces
    // is still the whole of what a caller without a `detail` reads.
    const baseMessage = `unsupported request: ${method} ${url}`;
    super(
      options?.detail === undefined
        ? baseMessage
        : `${baseMessage}: ${options.detail}`,
      method,
      url,
      options,
    );
    hide(this, 'name', 'UnsupportedRequestError');
  }
}

/**
 * An unknown body type was passed: the body could not be turned into something
 * sendable. Not retryable.
 */
export class UnsupportedBodyError extends HttpError {
  /**
   * Always `'unsupported'`, shared with `UnsupportedRequestError`: on a
   * dashboard both mean "we could not make this call", and which half of the
   * call was at fault is the class rather than the kind.
   */
  readonly failureKind = 'unsupported';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`unsupported body: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'UnsupportedBodyError');
  }
}

/**
 * The outgoing options failed `validate.request`. Not retryable: the same
 * options would fail again. `cause` is the error the validator threw.
 */
export class RequestValidationError extends HttpError {
  /**
   * Always `'validation'`, shared with `ResponseValidationError`: the kind says
   * a validator rejected something, the class says which side it was.
   */
  readonly failureKind = 'validation';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`request validation failed: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'RequestValidationError');
  }
}

/* ---------------------------------------------------------------------------
 * Held back by one of our own limiters. Neither of the two below is an
 * `HttpError`: both guards live in `@ns/resilience`, which knows nothing about
 * HTTP, so what they raise carries `key` and the `isPolicyError` marker instead
 * of `failureKind`, `status` and `url`.
 * ------------------------------------------------------------------------- */

/**
 * The breaker is open. Not retryable: that is precisely what open means.
 *
 * @remarks
 * NOT an `HttpError`, and `isHttpError` answers `false` for it. The circuit
 * guard lives in `@ns/resilience`, which knows nothing about HTTP, so its
 * refusal carries no `failureKind`, no `status` and no `url`. What it carries
 * instead is `key`, which this package fills with the method and the address it
 * refused, `openedAt` for the moment the circuit opened, and the `isPolicyError`
 * marker from that package.
 *
 * Re-exported here rather than redeclared, so `instanceof` still answers `true`
 * for the very class the guard raises, and `isCircuitOpenError` still
 * recognizes one.
 */
export { CircuitOpenError } from '@ns/resilience';

/**
 * No slot and no queue. Not retryable: the queue would still be full.
 *
 * @remarks
 * NOT an `HttpError`, for the same reason and with the same consequences as
 * `CircuitOpenError` above: `isHttpError` answers `false`, and what it carries
 * instead is `key`, filled by this package with the method and the address it
 * refused, plus the `isPolicyError` marker.
 *
 * `errors.test.ts` holds the COUNT of failures outside the hierarchy rather than
 * this sentence, so a third escapee shows up whether or not anybody remembered
 * to write it down.
 *
 * Re-exported here rather than redeclared, so `instanceof` still answers `true`
 * for the very class the guard raises.
 */
export { BulkheadRejectedError } from '@ns/resilience';

/**
 * Whether `value` is a refusal raised by one of the guards, such as a
 * `CircuitOpenError` or a `BulkheadRejectedError`.
 *
 * @remarks
 * Re-exported from `@ns/resilience` rather than redeclared, so a caller can
 * tell our own refusals from an `HttpError` without taking a second
 * dependency, and so the check keys on the very marker the guards set.
 */
export { isPolicyError } from '@ns/resilience';

/* ---------------------------------------------------------------------------
 * The upstream answered. `http`, and `status` carries the number.
 *
 * The eight below hang off `HttpRequestError`, so none of them declares a kind:
 * the branch already is one.
 *
 * Each fills in its own status when the caller leaves the options out, because
 * the class name has already said which one it is — a `NotFoundError` that made
 * you spell out 404 would be asking for the half of the answer it holds itself.
 * A status passed in still wins, which is what `ServerError` needs: one class
 * covers the whole 5xx range.
 * ------------------------------------------------------------------------- */

/** 400. Not retryable. */
export class BadRequestError extends HttpRequestError {
  constructor(method: string, url: string, options?: FixedStatusOptions) {
    super(`bad request: ${method} ${url}`, method, url, {
      ...options,
      status: 400,
    });
    hide(this, 'name', 'BadRequestError');
  }
}

/** 401. Not retryable. */
export class UnauthorizedError extends HttpRequestError {
  constructor(method: string, url: string, options?: FixedStatusOptions) {
    super(`unauthorized: ${method} ${url}`, method, url, {
      ...options,
      status: 401,
    });
    hide(this, 'name', 'UnauthorizedError');
  }
}

/** 403. Not retryable. */
export class ForbiddenError extends HttpRequestError {
  constructor(method: string, url: string, options?: FixedStatusOptions) {
    super(`forbidden: ${method} ${url}`, method, url, {
      ...options,
      status: 403,
    });
    hide(this, 'name', 'ForbiddenError');
  }
}

/** 404. Not retryable. */
export class NotFoundError extends HttpRequestError {
  constructor(method: string, url: string, options?: FixedStatusOptions) {
    super(`not found: ${method} ${url}`, method, url, {
      ...options,
      status: 404,
    });
    hide(this, 'name', 'NotFoundError');
  }
}

/** 405. Not retryable. */
export class MethodNotAllowedError extends HttpRequestError {
  constructor(method: string, url: string, options?: FixedStatusOptions) {
    super(`method not allowed: ${method} ${url}`, method, url, {
      ...options,
      status: 405,
    });
    hide(this, 'name', 'MethodNotAllowedError');
  }
}

/** 422. Not retryable. */
export class UnprocessableContentError extends HttpRequestError {
  constructor(method: string, url: string, options?: FixedStatusOptions) {
    super(`unprocessable content: ${method} ${url}`, method, url, {
      ...options,
      status: 422,
    });
    hide(this, 'name', 'UnprocessableContentError');
  }
}

/** 5xx. Retryable for 502, 503 and 504, not for the rest. */
export class ServerError extends HttpRequestError {
  constructor(method: string, url: string, options?: HttpRequestErrorOptions) {
    super(`server error: ${method} ${url}`, method, url, {
      status: 500,
      ...options,
    });
    hide(this, 'name', 'ServerError');
  }
}

/**
 * The options for a class that is NAMED after its status.
 *
 * @remarks
 * `status` is omitted rather than ignored. A class called `NotFoundError`
 * already knows its number, so naming one at the call site can only be right by
 * accident or wrong on purpose -- and the type required it, so every caller had
 * to name it. Omitting makes the mistake unspeakable instead of tolerated, and
 * the constructor puts its own status AFTER the spread so a value that arrives
 * through a variable cannot win either.
 */
export type FixedStatusOptions = Omit<HttpRequestErrorOptions, 'status'>;

/** The bag of `HttpThrottleError`, which is the only class that adds a field. */
export interface HttpThrottleErrorOptions extends FixedStatusOptions {
  /**
   * How long to wait, in milliseconds. Flat rather than a `retryAfter` object:
   * a caller wants one number to hand to a timer, and the `retry-after` header
   * comes in two spellings (seconds, or an HTTP date) that both collapse to it.
   */
  retryAfterMs?: number;
}

/** 429. Retryable, and it says when. */
export class HttpThrottleError extends HttpRequestError {
  /**
   * How long the upstream asked us to wait, in milliseconds, ready to hand to a
   * timer. `undefined` when it sent no `retry-after`, or one this library could
   * make no number of.
   *
   * @remarks
   * Already normalized, which is the point of the field: `retry-after` comes as
   * delta-seconds or as an HTTP date, and both collapse to this one number so
   * the unit is stated once here instead of at every call site. A date is
   * resolved against the clock at the moment the failure was built, so a value
   * that has sat in a queue is that much stale.
   *
   * The parse is the grammar of RFC 9110 section 10.2.3 and nothing wider, so a
   * value in neither form leaves the field absent rather than turning into a
   * number nobody sent. What that rules out is mostly the accidental: an empty
   * or whitespace header used to become a wait of `0`, and `'1e3'` a wait of
   * sixteen minutes.
   *
   * A number that IS in the grammar is still the upstream's word and not this
   * library's. Nothing caps it here, so clamp it yourself before handing it to a
   * timer if an absurd value would hurt.
   */
  declare readonly retryAfterMs?: number;

  constructor(method: string, url: string, options?: HttpThrottleErrorOptions) {
    super(`throttled: ${method} ${url}`, method, url, {
      ...options,
      status: 429,
    });
    hide(this, 'name', 'HttpThrottleError');
    // Installed only when there is one. A plain field declaration defines the
    // property at construction even when nothing assigns to it, which put an
    // empty `retryAfterMs` in every spread and disagreed with `logFields()`.
    if (options?.retryAfterMs !== undefined) {
      Object.defineProperty(this, 'retryAfterMs', {
        configurable: true,
        enumerable: true,
        value: options.retryAfterMs,
        writable: true,
      });
    }
  }

  /**
   * The base fields plus `retryAfterMs`, when the upstream named one.
   *
   * @remarks
   * This class is the one exception to "the enumerable set IS the log field
   * set": the field is installed enumerable rather than through `hide()`, so a
   * spread carries it too. That is deliberate. A wait the upstream dictated
   * cannot be derived from anything else in the set, which is exactly the test
   * `statusText` fails.
   *
   * Omitted rather than sent as `undefined` when there was no `retry-after`, so
   * a log line does not carry an empty key.
   * @throws Whatever `sanitize` throws. It is your function and it is called
   *   unguarded, so a sanitizer that fails takes down the catch block that was
   *   trying to log the failure. `toJSON()` cannot hit that one: it takes no
   *   sanitizer and passes none on.
   * @remarks
   * Reading the `cause` chain does NOT throw, and that is enforced rather than
   * hoped for. Flattening it reads `message`, `name`, `code`, `errno`,
   * `syscall` and `cause` off values that came from outside this library, so a
   * throwing getter or a `Proxy` trap among them is expected. A link that
   * RESISTS being read yields an empty link, and a link that LIES between two
   * reads cannot: every property is read once and the check is on what came
   * back. `toJSON()` and
   * `JSON.stringify(error)` go through here and inherit that.
   * @privateRemarks
   * `ER55`.
   */
  override logFields(sanitize?: SanitizeErrorFields): HttpErrorLogFields {
    const fields: HttpErrorLogFields = { ...super.logFields() };
    if (this.retryAfterMs !== undefined)
      fields.retryAfterMs = this.retryAfterMs;
    return sanitize ? sanitize(fields) : fields;
  }
}

/* ---------------------------------------------------------------------------
 * Something happened to the call in flight, or to what came back.
 * ------------------------------------------------------------------------- */

/**
 * The transport never came back with a status: DNS, TLS, a refused connection,
 * or a `fetch` that rejected for any other reason. Not retryable by default: the
 * default loop repeats statuses and this failure has none.
 *
 * @remarks
 * `network` rather than `http`, because there was no response to have a status
 * — the difference between "the upstream said no" and "the upstream was never
 * reached", which is the split a dashboard needs.
 *
 * Always wraps whatever the transport threw, so a bare `TypeError: fetch failed`
 * keeps its reason in `cause` while the caller and the `after` handlers see one
 * recognizable failure. Without it a rejected transport reached an observer as a
 * value the library could say nothing about, and `error` had to stay empty on the
 * one failure it most obviously applies to.
 *
 * A caller who wants it repeated sets
 * `retry.networkErrors: true`, or decides on `failureKind === 'network'` in
 * an own `shouldRetry`.
 */
export class NetworkError extends HttpError {
  /**
   * Always `'network'`: nothing came back, so there is no status to group on.
   * The one kind that separates "the upstream was never reached" from
   * `'http'`'s "the upstream said no", which is the split that tells a network
   * problem from an application one.
   */
  readonly failureKind = 'network';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`network request failed: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'NetworkError');
  }
}

/**
 * Our own deadline elapsed. Not retryable by default: the default loop repeats
 * statuses and this failure has none.
 *
 * @remarks
 * A repeat would get a fresh deadline. An own `shouldRetry` is what turns it on.
 */
export class RequestTimeoutError extends HttpError {
  /**
   * Always `'timeout'`, and always OUR deadline: an upstream that answers 504
   * is `'http'`. A rise in these is a signal about the budget we set as much as
   * about the upstream.
   */
  readonly failureKind = 'timeout';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`request timed out: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'RequestTimeoutError');
  }
}

/**
 * One attempt outran `perAttemptTimeoutMs` while the call still had clock left.
 * Retryable by default: the one failure without a status that the default loop
 * repeats.
 *
 * @remarks
 * EXTENDS `HttpError` AND DELIBERATELY NOT `RequestTimeoutError`, even though
 * the two read alike. Were it a subclass, `failure instanceof
 * RequestTimeoutError` would answer `true` for both, and a caller that gives up
 * on an exhausted budget would also give up on an attempt the loop was still
 * willing to repeat. The two mean opposite things about what happens next:
 * `RequestTimeoutError` is "your budget is gone and nothing more is coming",
 * this one is "that attempt took too long, another is on its way".
 *
 * REPEATED BY THE DEFAULT LOOP, which is the one exception to the rule that a
 * failure carrying no status is not repeated. That rule exists because a
 * network error or an elapsed call deadline says nothing a second attempt could
 * improve on; an attempt clock says the opposite, since bounding one attempt is
 * pointless if the loop then stops on it. Without the knob a server that
 * accepts the connection and goes silent spends the whole deadline on attempt
 * one and the configured retries never happen.
 *
 * A caller sees this only when the LAST attempt is the one that ran out. Every
 * earlier one is swallowed by the retry that follows it.
 */
export class AttemptTimeoutError extends HttpError {
  /**
   * Always `'attempt-timeout'`, its own kind rather than `'timeout'`: a
   * `shouldRetry` that decides on the kind alone must be able to repeat this
   * one while still giving up on an exhausted call deadline, and one shared
   * kind would put both in a single bucket.
   */
  readonly failureKind = 'attempt-timeout';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`attempt timed out: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'AttemptTimeoutError');
  }
}

/**
 * The caller aborted. Not retryable, and here that is stronger than usual:
 * retrying is exactly what the caller asked us not to do.
 */
export class RequestAbortError extends HttpError {
  /**
   * Always `'abort'`: the one kind that is not a fault. The caller asked for
   * this, so it is the member to filter OUT of an error rate before the number
   * means anything.
   */
  readonly failureKind = 'abort';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`request aborted: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'RequestAbortError');
  }
}

/**
 * The body arrived but could not be turned into what was asked for: broken
 * JSON, or a `deserializer` that threw. Not retryable — the same bytes would
 * fail the same way.
 *
 * @remarks
 * Distinct from a validation failure, which is about a body that decoded fine
 * and only then turned out to have the wrong shape. Always wraps whatever
 * spotted it, so `JSON.parse`'s own message survives in `cause`.
 */
export class ResponseDecodeError extends HttpError {
  /**
   * Always `'parse'`: the bytes arrived and would not decode at all. Split out
   * from `'validation'`, which is a body that decoded fine and only then turned
   * out to have the wrong shape -- the two point at different upstream faults.
   */
  readonly failureKind = 'parse';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`response decode failed: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'ResponseDecodeError');
  }
}

/**
 * A successful decoded response produced no value while one was required.
 * Pass `allowEmptyResponse: true` when no content is expected.
 */
export class EmptyResponseError extends HttpError {
  /**
   * Always `'parse'`: the successful response carried no body where one was
   * required. It shares the kind with `ResponseDecodeError`, so a `shouldRetry`
   * that decides on the kind treats both alike; when an empty answer is expected,
   * set `allowEmptyResponse: true` rather than retrying.
   */
  readonly failureKind = 'parse';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(
      `response has no decoded body: ${method} ${url}. Pass \`allowEmptyResponse: true\` when no content is expected.`,
      method,
      url,
      options,
    );
    hide(this, 'name', 'EmptyResponseError');
  }
}

/**
 * The decoded body failed `validate.response`. Not retryable. Distinct from
 * `ResponseDecodeError`: the body WAS valid JSON, it just had the wrong shape.
 * `cause` is the error the validator threw.
 */
export class ResponseValidationError extends HttpError {
  /**
   * Always `'validation'`, shared with `RequestValidationError`: a validator
   * rejected it, and the class says the response side. Distinct from `'parse'`,
   * which never got as far as a value to check.
   */
  readonly failureKind = 'validation';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`response validation failed: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'ResponseValidationError');
  }
}

/**
 * The `maxResponseBytes` cap was hit. Not retryable: the same answer would be
 * just as large.
 *
 * @remarks
 * It is raised from two places that answer the same question at different
 * costs — a `content-length` above the cap, before a byte is read, and a
 * counter over the stream that catches a length that was absent or understated.
 */
export class ResponseTooLargeError extends HttpError {
  /**
   * Always `'too-large'`, its own kind rather than `'parse'`: reading stopped on
   * purpose because `maxResponseBytes` was hit, so it says something about the
   * cap we set and not about what the upstream sent being broken.
   */
  readonly failureKind = 'too-large';

  constructor(method: string, url: string, options?: HttpErrorOptions) {
    super(`response too large: ${method} ${url}`, method, url, options);
    hide(this, 'name', 'ResponseTooLargeError');
  }
}

/* ---------------------------------------------------------------------------
 * The typeguards.
 * ------------------------------------------------------------------------- */

/**
 * Is this a failure this library produced? Duck-typed alternative to
 * `instanceof`, robust across module boundaries. The five below are duck-typed
 * for the same reason.
 *
 * @remarks
 * A value built in one realm does not share a prototype chain with the global
 * of another, so `instanceof Error` answers `false` for something that plainly
 * IS an error. Two realms is the normal case here, not an exotic one: the test
 * file and the module under test already are two.
 *
 * A caller branching on `instanceof NotFoundError` is therefore fine within one
 * realm and silently wrong across two, while `isHttpError` holds either way.
 *
 * @privateRemarks
 * MEASURED in this repo, under Jest in a node VM:
 *
 *   new TypeError('x') instanceof Error     -> false
 *   new TypeError('x') instanceof TypeError -> true
 *   Object.prototype.toString.call(e)       -> [object Error]
 *
 * That is why these exist and why they must stay duck-typed, and why the cost
 * of renaming one of these classes bites harder than it reads.
 *
 * @param value Whatever reached your `catch`. Nothing is assumed of it: a
 *   string, `null` and a value built in another realm all answer without
 *   throwing.
 * @returns `true` when the value carries a string `failureKind` and a string
 *   `url` -- the two fields every failure in this vocabulary has. Narrows to
 *   `HttpError`, so `failureKind`, `method`, `url` and `logFields()` are
 *   readable afterwards. It does NOT tell you whether the upstream answered;
 *   `isHttpRequestError` is that question.
 * @throws Whatever a property read on `value` throws. The check is a duck-typed
 *   read of `failureKind` and `url`, so a value with a throwing getter, or a
 *   `Proxy` whose `get` trap throws, propagates that out of the guard and into
 *   your catch block. A string, `null`, a null-prototype object and a value from
 *   another realm all answer without throwing.
 */
export function isHttpError(value: unknown): value is HttpError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { failureKind?: unknown }).failureKind === 'string' &&
    typeof (value as { url?: unknown }).url === 'string'
  );
}

/**
 * Did an answer come back, so is `status` there to read without a check?
 *
 * @privateRemarks
 * The narrow half of a question that used to be one: `isHttpError` asks "is
 * this a failure of ours", this one asks "did the upstream answer". Before the
 * split there was a single guard and no way to tell those apart, so a catch
 * site reading `status` was reading an optional field that half the vocabulary
 * could never fill.
 *
 * `status` is the discriminant rather than a class list, for the realm reason
 * above: it is a property every member of this branch has and no member of the
 * other one does.
 * @param value Whatever reached your `catch`, on the same terms as
 * `isHttpError`.
 * @returns `true` when a numeric `status` is there, which narrows to
 * `HttpRequestError` and makes reading it unconditional. `false` for the
 * failures that carry none: a network failure, a timeout, a caller abort and
 * a refusal by the breaker or the bulkhead. Also `false` for a decode, an
 * empty-body, a validation and a too-large failure -- those happen on a
 * response that DID arrive, so absence here is not proof that the upstream
 * stayed silent.
 * @throws Whatever a property read on `value` throws, on the same terms as
 *   `isHttpError`.
 */
export function isHttpRequestError(value: unknown): value is HttpRequestError {
  return (
    isHttpError(value) && 'status' in value && typeof value.status === 'number'
  );
}

/*
 * The four below narrow on `name`, and one rule covers all four rather than
 * several rules covering one or two each.
 *
 * For two of them `failureKind` genuinely cannot do the job. `HttpThrottleError`
 * inherits `'http'` and shares it with eight siblings. `CircuitOpenError` has no
 * `failureKind` at all any more: it left the hierarchy with the guard that
 * raises it, which is exactly why its guard below asks `isPolicyError` and the
 * other three ask `isHttpError`. Narrowing on `name` is what let the rest of
 * that rule stay put through the move.
 *
 * For the other two `failureKind` could -- MEASURED, only `RequestTimeoutError`
 * carries `'timeout'` and only `RequestAbortError` carries `'abort'` -- and they
 * use `name` anyway, because a guard that reads differently from its neighbour
 * invites the reader to wonder what the difference means.
 *
 * `name` is set by every constructor involved and travels with the value across
 * a realm boundary, which is the whole reason these are duck-typed.
 */

/**
 * Duck-typed alternative to `instanceof HttpThrottleError`, which answers `false`
 * across a realm boundary for a value that plainly is one.
 *
 * @param value Whatever reached your `catch`. It asks `isHttpError` first, so a
 *   non-object, `null` and a value from another realm all answer `false`
 *   instead of throwing.
 * @returns `true` for the 429, narrowed so that `retryAfterMs` is readable --
 *   already normalized to milliseconds, and `undefined` when the upstream sent
 *   no `retry-after` in either spelling. Narrowed on `name` rather than on
 *   `failureKind`, which cannot separate this one: it inherits `'http'` from
 *   `HttpRequestError` and shares it with the eight other classes in that
 *   family.
 * @throws Whatever a property read on `value` throws, on the same terms as
 *   `isHttpError`.
 */
export function isHttpThrottleError(
  value: unknown,
): value is HttpThrottleError {
  return isHttpError(value) && value.name === 'HttpThrottleError';
}

/**
 * Duck-typed alternative to `instanceof RequestTimeoutError`, which answers `false`
 * across a realm boundary for a value that plainly is one.
 *
 * @param value Whatever reached your `catch`. It asks `isHttpError` first, so a
 *   non-object, `null` and a value from another realm all answer `false`
 *   instead of throwing.
 * @returns `true` when OUR deadline elapsed, and `false` when the caller's own
 *   signal fired first. Both arrive at the transport as an abort, so this and
 *   `isRequestAbortError` are what separate them -- which is the same
 *   distinction a breaker makes when it counts a timeout and ignores a caller
 *   abort.
 * @throws Whatever a property read on `value` throws, on the same terms as
 *   `isHttpError`.
 */
export function isRequestTimeoutError(
  value: unknown,
): value is RequestTimeoutError {
  return isHttpError(value) && value.name === 'RequestTimeoutError';
}

/**
 * Duck-typed alternative to `instanceof RequestAbortError`, which answers `false`
 * across a realm boundary for a value that plainly is one.
 *
 * @param value Whatever reached your `catch`. It asks `isHttpError` first, so a
 *   non-object, `null` and a value from another realm all answer `false`
 *   instead of throwing.
 * @returns `true` when the CALLER's signal ended the call, the mirror of
 *   `isRequestTimeoutError`. An `AbortSignal.timeout(n)` the caller passed
 *   counts as their abort and lands here, not on the timeout guard: the two are
 *   told apart by which signal fired and not by what its reason is called.
 * @throws Whatever a property read on `value` throws, on the same terms as
 *   `isHttpError`.
 */
export function isRequestAbortError(
  value: unknown,
): value is RequestAbortError {
  return isHttpError(value) && value.name === 'RequestAbortError';
}

/**
 * The circuit guard's own recognizer, re-exported so a caller of this library
 * keeps importing it from here.
 *
 * @privateRemarks
 * The doc a caller reads lives on the definition in `@ns/resilience`, because
 * the generator follows a re-export to its source and never renders this block.
 * MEASURED: a copy here replaced the caller-facing text in `api.md` with the
 * source's, so two blocks means the shorter one silently wins.
 *
 * It asks `isPolicyError` and NOT `isHttpError`, and that swap was the whole of
 * this guard's move. A breaker refusal stopped being an `HttpError` when the
 * circuit guard moved to `@ns/resilience`, so the old first half would have
 * answered `false` for every refusal there is, for ever, with no compile error:
 * the `name` half still matches, and a guard that answers `false` breaks
 * nothing loudly. `breaker-builder.test.ts` BK3 is the owner of that.
 */
export { isCircuitOpenError } from '@ns/resilience';
